/**
 * Button (§5.1). Always a native <button>. Disabled-with-reason buttons stay focusable
 * (`aria-disabled`), describe why via `aria-describedby`, and explain on tap.
 */
import { useId, type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import { haptic } from '../state/settingsStore';
import { toast } from '../state/toastStore';
import { cx } from './cx';
import { Icon, type IconName } from './Icon';
import { PixelSpinner } from './PixelSpinner';
import { buttonClass, type ButtonSize, type ButtonVariant } from './buttonClass';
import styles from './Button.module.css';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  trailingIcon?: IconName;
  /** Trailing meta, e.g. "3 mana". */
  meta?: ReactNode;
  block?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  /** Hard-disabled (not focusable). Prefer `disabledReason` so players learn why. */
  disabled?: boolean;
  /** Soft-disabled with an explanation, e.g. "Rally needs 3 mana. You have 1." */
  disabledReason?: string | null;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'secondary',
  size = 'm',
  icon,
  trailingIcon,
  meta,
  block,
  loading,
  loadingLabel = 'Sending…',
  disabled,
  disabledReason,
  className,
  children,
  onClick,
  type = 'button',
  ref,
  ...rest
}: ButtonProps) {
  const reasonId = useId();
  const softDisabled = !!disabledReason || !!loading;
  return (
    <>
      <button
        {...rest}
        ref={ref}
        type={type}
        disabled={disabled}
        aria-disabled={softDisabled || undefined}
        aria-busy={loading || undefined}
        aria-describedby={cx(rest['aria-describedby'], disabledReason ? reasonId : undefined) || undefined}
        className={cx(buttonClass(variant, size, block), softDisabled && styles.softDisabled, className)}
        onClick={(event) => {
          if (loading) return;
          if (disabledReason) {
            event.preventDefault();
            toast.info(disabledReason);
            return;
          }
          // A tiny tick on primary actions where the device and settings allow it.
          if (variant === 'primary' || variant === 'danger-fill') haptic(8);
          onClick?.(event);
        }}
      >
        {loading ? (
          <>
            <PixelSpinner />
            <span>{loadingLabel}</span>
          </>
        ) : (
          <>
            {icon && <Icon name={icon} size={20} />}
            {children !== undefined && <span className={styles.label}>{children}</span>}
            {meta !== undefined && <span className={styles.meta}>· {meta}</span>}
            {trailingIcon && <Icon name={trailingIcon} size={20} />}
          </>
        )}
      </button>
      {disabledReason && (
        <span id={reasonId} className="wb-visually-hidden">
          {disabledReason}
        </span>
      )}
    </>
  );
}

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  icon: IconName;
  label: string;
  variant?: 'raised' | 'plain' | 'accent';
  badge?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

/** 44×44 square icon button. `label` becomes the accessible name. */
export function IconButton({
  icon,
  label,
  variant = 'raised',
  badge,
  className,
  type = 'button',
  ref,
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(styles.iconButton, styles[`icon-${variant}`], className)}
    >
      <Icon name={icon} size={22} />
      {badge !== undefined && badge !== null && <span className={styles.badge}>{badge}</span>}
    </button>
  );
}
